import _ from "lodash";
import { getHealthCenterRules } from "../utils/hooks/useHealthCenterRules";
import {
  canPrescribeFrom,
  hasVademecumMedicines,
  VADEMECUM,
} from "../modules/prescriptions/new/form/medicine/prescriptionMethods";

const patientMedicalDocumentsDefaultFilter = patient => ({ patient: { id: patient.id } });

class User {
  constructor(userJson) {
    _.assign(this, userJson);
  }

  can(action, resource) {
    return _(this.scopes)
      .map((scope) =>
        _(scope)
          .split(".")
          .thru(([resource, action]) => ({ resource, action }))
          .value()
      )
      .find(
        ({ action: scopeAction, resource: scopeResource }) =>
          action === scopeAction && _.includes(scopeResource, resource)
      );
  }

  canCancel({ type }) {
    return this.can("cancel", type);
  }

  canRepeat({ type, medicines }) {
    return (
      this.can("repeat", type) &&
      (type !== "prescription" ||
        !hasVademecumMedicines(medicines) ||
        this.canPrescribeFrom(VADEMECUM))
    );
  }

  canPrescribeFrom(method) {
    return canPrescribeFrom(this.prescriptionMethods, method);
  }


  filterPatients(patients) {
    return patients;
  }

  showPatientShareInfo() {
    return true;
  }
}

class DoctorUser extends User {
  landingRoute() {
    return "/";
  }
  // From gender (f/m), not the title: titles are now profession-derived (Od, Lic, Téc...) and
  // carry no gender. A doctor api hasn't got a gender for yet keeps the legacy title rule; "o"
  // gets no article.
  getArticle() {
    return { m: "El", f: "La", o: "" }[this.gender] ?? (this.title === "Dr" ? "El" : "La");
  }

  getPreposition() {
    return { m: "del", f: "de la", o: "de" }[this.gender] ?? (this.title === "Dr" ? "del" : "de la");
  }

  // A title can be empty (a profession without one) — no leading ". ".
  fullNameWithTitle() {
    return _.compact([this.title && `${this.title}.`, this.name, this.surname]).join(" ");
  }

  patientsDisplayNames() {
    return {
      title: _.trim(`${this.getArticle()} ${this.fullNameWithTitle()}`),
      preposition: `${this.getPreposition()} ${this.fullNameWithTitle()}`,
    };
  }

  patientMedicalDocumentsFilters(patient) {
    return patientMedicalDocumentsDefaultFilter(patient);
  }

  showPatientShareInfo() {
    const healthCenterId = this.profile?.healthCenter?.id;
    const shareInfoRules = getHealthCenterRules(healthCenterId, "patients.detail.shareInfo");
    return !shareInfoRules?.hide;
  }
}

class AssistantUser extends User {
  //TODO rename prescriptions => medical-documents
  landingRoute() {
    return "/prescriptions";
  }
  patientsDisplayNames() {
    const healthCenterName = this.myHealthCenter.name;
    return {
      title: `El centro ${healthCenterName}`,
      preposition: `del centro ${healthCenterName}`,
    };
  }

  patientMedicalDocumentsFilters(patient) {
    if (this.myHealthCenter.independentDoctors) {
      return { patient: { documentNumber: patient.documentNumber } };
    }
    return patientMedicalDocumentsDefaultFilter(patient);
  }

  filterPatients(patients) {
    if (this.myHealthCenter.independentDoctors) {
      return _.uniqBy(patients, (pat) => pat.documentNumber);
    }
    return patients;
  }
}

export const toReifiedUser = (userJson) => {
  const UserTypeMap = {
    doctor: DoctorUser,
    assistant: AssistantUser,
  };

  const UserType = _.get(UserTypeMap, userJson.type);
  if (!UserType) throw new Error("invalid user type");

  return new UserType(userJson);
};
